import { useState, useEffect } from 'react';
import { Mail, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { buildLoginRedirectUrl } from '../services/appUrls';
import { trackLoginStart } from '../services/analytics';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6">
        <nav className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <Mail className="h-8 w-8 text-blue-600 mr-2" />
            <span className="text-xl font-bold text-blue-600">Uumail</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#how-it-works" className="text-gray-700 hover:text-blue-600 transition-colors">
              {t('nav.howItWorks')}
            </a>
            <a href="#features" className="text-gray-700 hover:text-blue-600 transition-colors">
              {t('nav.features')}
            </a>
            <a href="#pricing" className="text-gray-700 hover:text-blue-600 transition-colors">
              {t('nav.pricing')}
            </a>
            <a href="https://uu.me/blog/" className="text-gray-700 hover:text-blue-600 transition-colors">
              {t('nav.blog')}
            </a>
            <a href="#faq" className="text-gray-700 hover:text-blue-600 transition-colors">
              {t('nav.faq')}
            </a>
            <LanguageSwitcher />
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('nav.dashboard')}
            </Link>
          ) : (
            <a
              href={buildLoginRedirectUrl()}
              onClick={trackLoginStart}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('auth.signIn')}
            </a>
          )}
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden" onClick={toggleMenu}>
            {isMenuOpen ? (
              <X className="h-6 w-6 text-gray-700" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700" />
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4">
            <div className="flex flex-col space-y-4">
              <a
                href="#how-it-works"
                className="text-gray-700 hover:text-blue-600 transition-colors"
                onClick={toggleMenu}
              >
                {t('nav.howItWorks')}
              </a>
              <a
                href="#features"
                className="text-gray-700 hover:text-blue-600 transition-colors"
                onClick={toggleMenu}
              >
                {t('nav.features')}
              </a>
              <a
                href="#pricing"
                className="text-gray-700 hover:text-blue-600 transition-colors"
                onClick={toggleMenu}
              >
                {t('nav.pricing')}
              </a>
              <a
                href="https://uu.me/blog/"
                className="text-gray-700 hover:text-blue-600 transition-colors"
                onClick={toggleMenu}
              >
                {t('nav.blog')}
              </a>
              <a
                href="#faq"
                className="text-gray-700 hover:text-blue-600 transition-colors"
                onClick={toggleMenu}
              >
                {t('nav.faq')}
              </a>
              <div className="py-2">
                <LanguageSwitcher />
              </div>
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-center"
                  onClick={toggleMenu}
                >
                  {t('nav.dashboard')}
                </Link>
              ) : (
                <a
                href={buildLoginRedirectUrl()}
                onClick={() => { trackLoginStart(); toggleMenu(); }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-center"
                >
                  {t('auth.signIn')}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
